import { useEffect, useRef, useState } from 'react';

import { useRefreshStore } from '../store/refresh';
import { useSessionStore } from '../store/session';
import { Spinner } from './ui';

/**
 * 당겨서 새로고침 — iOS 앱의 그 동작.
 *
 * 홈 화면 앱(standalone)엔 새로고침 버튼이 없어서 사실상 필수다.
 * 맨 위(scrollY=0)에서 아래로 당기면 헤더 아래 인디케이터가 차오르고,
 * 문턱을 넘겨 놓으면 refresh tick을 올린다(화면들이 구독해 재조회).
 *
 * 콘텐츠를 직접 밀지 않는다 — iOS의 네이티브 고무줄 바운스가 이미 그 역할을
 * 하므로, 우리는 인디케이터만 얹는다(이중 변환으로 인한 잔떨림 방지).
 */

const THRESHOLD = 64; // 이만큼 당기면 발동(px, 감쇠 적용 후)
const DAMPING = 0.42; // 손가락 이동 대비 인디케이터 반응 비율

export function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef<number | null>(null);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);
  const bump = useRefreshStore((s) => s.bump);

  useEffect(() => {
    const setPullBoth = (v: number) => {
      pullRef.current = v;
      setPull(v);
    };

    const onStart = (e: TouchEvent) => {
      // 오버레이(사진 뷰어·게임 스테이지)가 body 스크롤을 잠근 동안은 완전히 비켜선다 —
      // 안 그러면 뷰어의 '쓸어내려 닫기'가 새로고침을 발동시켜 화면이 깜빡인다.
      if (document.body.style.overflow === 'hidden') {
        startYRef.current = null;
        return;
      }
      // 맨 위에서 시작한 제스처만 후보. 새로고침 중에는 무시.
      startYRef.current =
        window.scrollY <= 0 && !refreshingRef.current ? (e.touches[0]?.clientY ?? null) : null;
    };

    const onMove = (e: TouchEvent) => {
      if (startYRef.current === null) return;
      const dy = (e.touches[0]?.clientY ?? 0) - startYRef.current;
      if (dy > 0 && window.scrollY <= 0) {
        setPullBoth(Math.min(THRESHOLD * 1.5, dy * DAMPING));
      } else {
        setPullBoth(0);
      }
    };

    const onEnd = () => {
      startYRef.current = null;
      if (pullRef.current >= THRESHOLD) {
        refreshingRef.current = true;
        setRefreshing(true);
        setPullBoth(THRESHOLD);
        bump();
        void useSessionStore.getState().refreshMe();
        // 재조회는 각 화면이 알아서 한다 — 인디케이터는 잠깐 보여주고 접는다.
        setTimeout(() => {
          refreshingRef.current = false;
          setRefreshing(false);
          setPullBoth(0);
        }, 800);
      } else {
        setPullBoth(0);
      }
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [bump]);

  const progress = Math.min(1, pull / THRESHOLD);
  if (pull <= 0 && !refreshing) return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-16 z-30 flex justify-center sm:hidden"
      style={{ opacity: refreshing ? 1 : progress }}
    >
      <div className="flex size-9 items-center justify-center rounded-full bg-surface shadow-raised">
        {refreshing ? (
          <Spinner />
        ) : (
          <span
            className="text-[15px] text-ink-500 transition-transform duration-150"
            // 문턱을 넘기면 화살표가 뒤집힌다 — "놓으면 새로고침" 신호.
            style={{ transform: `rotate(${progress >= 1 ? 180 : 0}deg)` }}
          >
            ↓
          </span>
        )}
      </div>
    </div>
  );
}
